import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { estimateKrwPrice } from '../../lib/aliexpress';
import type { AliExpressHotProduct } from '../../types';

interface SearchBarProps {
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

// Korean to English keyword mapping for better AliExpress results
const koreanToEnglishKeywords: Record<string, string> = {
  '컴퓨터': 'computer PC desktop',
  '게이밍': 'gaming',
  '그래픽카드': 'graphics card GPU RTX GTX',
  '노트북': 'laptop notebook',
  '데스크탑': 'desktop PC computer',
  '프로세서': 'processor CPU Ryzen Intel',
  '메모리': 'memory RAM DDR4 DDR5',
  '저장장치': 'storage SSD HDD NVMe',
  '메인보드': 'motherboard mainboard',
  '파워서플라이': 'power supply PSU',
  '케이스': 'computer case chassis',
  '모니터': 'monitor display screen',
  '키보드': 'keyboard mechanical',
  '마우스': 'mouse gaming wireless',
  '헤드셋': 'headset headphone gaming',
  '스피커': 'speaker audio',
  'CPU': 'CPU processor Ryzen Intel',
  'RAM': 'RAM memory DDR4 DDR5',
  'SSD': 'SSD NVMe storage',
  'HDD': 'HDD hard drive storage',
  'RTX': 'RTX graphics card NVIDIA',
  'GTX': 'GTX graphics card NVIDIA',
  '라즈베리파이': 'Raspberry Pi',
  '라즈베리': 'Raspberry Pi',
  '아두이노': 'Arduino',
};

function convertKoreanToEnglish(keywords: string): string {
  let result = keywords;
  for (const [korean, english] of Object.entries(koreanToEnglishKeywords)) {
    if (keywords.includes(korean)) {
      result = result.replace(korean, english);
    }
  }
  return result;
}

export function SearchBar({
  placeholder = '제품을 검색하세요...',
  className,
  autoFocus = false,
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<AliExpressHotProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const englishKeywords = convertKoreanToEnglish(query);
        const response = await fetch(
          `https://moapc-worker.linuxm.workers.dev/api/aliexpress/search?keywords=${encodeURIComponent(englishKeywords)}&pageSize=5`
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.products || []);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSearch = (searchQuery: string) => {
    if (searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setQuery('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const handleProductClick = (product: AliExpressHotProduct) => {
    // Navigate to product detail page with product data
    navigate(`/products/aliexpress/${product.productId}`, { state: { product } });
    setShowResults(false);
    setQuery('');
  };

  const hasResults = searchResults.length > 0;

  return (
    <div className={clsx('relative', className)}>
      <form onSubmit={handleSubmit} className="relative">
        <Search
          className={clsx(
            'absolute left-3 top-1/2 -translate-y-1/2 text-gray-400',
            'transition-colors',
            isFocused && 'text-orange-500'
          )}
          size={20}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.length >= 2) {
              setShowResults(true);
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => setIsFocused(false), 200);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={clsx(
            'w-full pl-10 pr-10 py-2.5',
            'border border-gray-300 rounded-lg',
            'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent',
            'transition-all',
            'placeholder:text-gray-400'
          )}
          aria-label="검색"
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="검색 지우기"
          >
            <X size={18} />
          </button>
        )}
      </form>

      {/* Autocomplete Results */}
      {showResults && query.length >= 2 && (
        <div
          ref={resultsRef}
          className={clsx(
            'absolute top-full left-0 right-0 mt-2',
            'bg-white rounded-lg shadow-lg border border-gray-200',
            'z-50 overflow-hidden',
            'animate-in slide-in-from-top-2 duration-200'
          )}
        >
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              검색 중...
            </div>
          ) : hasResults ? (
            <div className="max-h-96 overflow-y-auto">
              <div className="p-2">
                {searchResults.map((product) => (
                  <button
                    key={product.productId}
                    onClick={() => handleProductClick(product)}
                    className={clsx(
                      'w-full text-left p-3',
                      'hover:bg-orange-50 rounded-lg',
                      'transition-colors',
                      'flex items-center gap-3'
                    )}
                  >
                    {product.imageUrl && (
                      <img
                        src={product.imageUrl}
                        alt={product.productTitle}
                        className="w-12 h-12 object-cover rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">
                        {product.productTitle}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product.shopName || 'AliExpress'}
                      </p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="font-semibold text-orange-600 text-sm">
                          ₩{estimateKrwPrice(product.salePrice).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          ${product.salePrice}
                        </p>
                      </div>
                      <ChevronRight size={14} className="text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => handleSearch(query)}
                className="w-full p-3 text-center text-sm text-orange-600 hover:bg-orange-50 transition-colors font-medium"
              >
                "{query}" 검색 결과 모두 보기
              </button>
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              검색 결과가 없습니다
            </div>
          )}
        </div>
      )}
    </div>
  );
}
